import ApiRequestVO from "../../manager/api/ApiRequestVO";
import {IAnyObject, INumberObject} from "../../common/SimpleInterface";
import ApiError from "../../manager/api/ApiError";
import {FactoryID} from "../../model-factory/type-interface/ModelFactoryType";
import {IPopulateIDs} from "../../model-factory/type-interface/ModelFactoryInterface";
import {LocatePageListDataModel} from "./LocatePageListDataFactory";
import {getModelFactory} from "../../model-factory/ModelFactory";
import {LocateCountryDataModel} from "./LocateCountryDataFactory";
import {getManager} from "../../manager/Manager";
import {isNumeric} from "../../common/SiteHelper";
import {SearchBasicDataModel, SearchParams} from "../search/SearchBasicDataFactory";
import {LngLat, LngLatBounds} from "maplibre-gl";
import {PostType} from "../../manager/post/PostManager";
import LocatableDataFactory, {ILocatableDataChoices, LocatableDataChoice, LocatableDataModel} from "./LocatableDataFactory";
import {CountryCode} from "../../../module/osm/location/LocationType";

export type LocateReturnType = "object" | "reference_id" | "reference_object" | "basic_reference_object" | "points_with_ids";

export type LocateDataSortBy =
    "name" |
    "distance" |
    "north_west_distance";

export type LocateViewSortBy =
    "name" |
    "near_me" |
    "near_position" |
    "near_location";

export class LocateSearchParams extends SearchParams
{

    postType:PostType = null!;
    organisationID:number|null = null;
    sortBy:LocateDataSortBy = "name";
    countryCode:CountryCode|"" = "";
    latitude:number = 0;
    longitude:number = 0;
    requireLocation:boolean = false;


    applyModelConditions(populateIDs: IPopulateIDs)
    {
        super.applyModelConditions(populateIDs);

        this.postType = populateIDs.post_type ?? "organisation";
        this.countryCode = populateIDs.locate_country_code ?? ""
        this.latitude = populateIDs.locate_latitude ?? 0;
        this.longitude = populateIDs.locate_longitude ?? 0;
        this.requireLocation = !!populateIDs.locate_require_location;
    }
}

export class LocateDataChoice extends LocatableDataChoice
{
    referenceID:string = "";
    referenceNotFound:boolean = false;

    constructor(object:IAnyObject)
    {
        super(object);

        this.referenceID = object.reference_id ?? "";
        this.referenceNotFound = !this.referenceID;
        const latitude= parseFloat(object.latitude ?? 0);
        const longitude= parseFloat(object.longitude ?? 0);
        const boundsWidth = parseFloat(object.bounds_width ?? 0);
        const boundsHeight = parseFloat(object.bounds_height ?? 0);
        if(latitude || longitude){
            this.lngLat = new LngLat(longitude, latitude);
            this.lngLatBounds = getManager().location.determineBounds(latitude, longitude, boundsWidth, boundsHeight);
        }
        this.countryCode = (object.country_code ?? "").toLowerCase();
        this.postType = object.reference_type ?? null;

    }
}

export class LocateDataModel extends SearchBasicDataModel
{

    searchParams:LocateSearchParams = new LocateSearchParams();

    returnType:LocateReturnType = "object";
    pageNumber:number = 1;
    requestReferenceLimit:number = 0;
    choices:ILocatableDataChoices = {};
    withLocationChoiceOrder: string[]|null = null;
    activeWithLocationChoiceList: LocatableDataChoice[]|null = null;

    applyModelConditions()
    {
        super.applyModelConditions();

        this.returnType = this.populateIDs.locate_return_type ?? "object";
        this.pageNumber = Math.max(this.populateIDs.page_number ?? 0);
        if(this.returnType === "points_with_ids"){
            this.requestReferenceLimit = 100;
        }

    }

    getPopulateRequestVO(): ApiRequestVO|null
    {
        const requestVO = new ApiRequestVO(true);

        if(!this.searchParams.organisationID){
            requestVO.endpoint = "/post/"+this.searchParams.postType+"/list";
        } else {
            requestVO.endpoint = "/post/organisation/"+this.searchParams.organisationID+"/"+this.searchParams.postType+"/list";
        }
        requestVO.params.return_type = this.returnType;
        if(this.searchParams.countryCode){
            requestVO.params.meta_in = {
                country: [this.searchParams.countryCode]
            }
        }

        if(this.returnType === "points_with_ids"){
            requestVO.params = {
                return_type: this.returnType
            };
            return requestVO;
        }

        if(!this.searchParams.searchTermID){
            return null;
        }

        const searchTerm = this.searchParams.searchTerm ? encodeURIComponent(this.searchParams.searchTerm) : null;
        if(searchTerm){
            requestVO.params.post_title_or_meta_like = {
                search_terms: [searchTerm],
                meta_keys: ['address', 'county', 'postcode']
            }
        }

        requestVO.params.latitude = this.searchParams.latitude;
        requestVO.params.longitude = this.searchParams.longitude;
        requestVO.params.require_location = this.searchParams.requireLocation;

        if(this.searchParams.sortBy === "distance"){
            requestVO.params.orderby = "distance";
        }

        this.addPageParamsToRequest(requestVO);

        return requestVO;
    }

    getRequiredReferenceIDs(choiceList:LocatableDataChoice[]|null=null, limit?:number):string[]
    {
        limit = limit ?? this.requestReferenceLimit;
        const sourceModel = this.getSourceReferenceModel();
        if(!limit || !sourceModel){
            return [];
        }
        choiceList = choiceList ?? this.choiceList();
        const referenceIDs:string[] = [];
        for(let choice of choiceList){
            if(limit > 0 && referenceIDs.length === limit){
                break;
            }
            if(!(choice instanceof LocateDataChoice)){
                continue
            }
            if(!choice.referenceID || choice.referenceNotFound || referenceIDs.includes(choice.referenceID)){
                continue
            }
            const referenceChoice = sourceModel.choices[choice.referenceID] ?? null;
            if(referenceChoice){
                continue
            }
            if(sourceModel.failedAdditionalRequestedIDs.includes(choice.referenceID)){
                choice.referenceNotFound = true;
                continue
            }
            referenceIDs.push(choice.referenceID)
        }
        return referenceIDs;
    }

    applyReferenceChoices(choiceList?:LocatableDataChoice[]|null):LocatableDataChoice[]
    {
        choiceList = choiceList ? choiceList : this.choiceList();
        const sourceModel = this.getSourceReferenceModel();

        if(!sourceModel){
            return []
        }

        const referenceChoiceList:LocatableDataChoice[] = [];
        for(let choice of choiceList)
        {
            if(!(choice instanceof LocateDataChoice)){
                referenceChoiceList.push(choice);
                continue
            }
            if (choice.referenceID)
            {
                let referenceChoice = sourceModel!.choices[choice.referenceID] ?? null;
                if(referenceChoice){
                    referenceChoice = sourceModel!.cloneChoice(referenceChoice) as LocatableDataChoice;
                    this.choices[referenceChoice.id] = referenceChoice;
                    this.clearActiveChoiceList();
                    referenceChoiceList.push(choice);
                }
            }
        }
        return referenceChoiceList;
    }

    async requestReferences(choiceList?:LocatableDataChoice[]):Promise<LocatableDataChoice[]>
    {
        choiceList = choiceList ? choiceList : this.choiceList();

        return this.requirePopulated().then(() => {
            const referenceIDs = this.getRequiredReferenceIDs(choiceList);
            if(!referenceIDs.length){
                return this.applyReferenceChoices(choiceList);
            }
            this.populating = true;
            this.onDataModelChanged();
            const sourceModel = this.getSourceReferenceModel();
            console.log("sourceModel", sourceModel)
            return sourceModel!.requestAdditionalChoices(referenceIDs, true).then((fromReferenceChoiceList) => {
                const referenceChoices:ILocatableDataChoices = {};
                for(let referenceChoice of fromReferenceChoiceList as LocatableDataChoice[]){
                    referenceChoices[referenceChoice.id] = referenceChoice;
                }
                const referenceChoiceList:LocatableDataChoice[] = [];
                for(let choice of choiceList ?? []){
                    if(!(choice instanceof LocateDataChoice)){
                        referenceChoiceList.push(choice);
                        continue;
                    }
                    if(choice.referenceID){
                        if(referenceIDs.includes(choice.referenceID)){
                            let referenceChoice = referenceChoices[choice.referenceID] ?? null;
                            if(referenceChoice){
                                this.choices[referenceChoice.id] = referenceChoice;
                                referenceChoiceList.push(choice);
                            } else {
                                choice.referenceNotFound = true;
                            }
                        }
                    }
                }
                if(this.isPopulated()){
                    this.handlePopulated();
                    return referenceChoiceList;
                }
                return this.requirePopulated().then(() => {
                    return referenceChoiceList;
                });
            })
        });
    }

    async requireReference(choiceID:string):Promise<void>
    {
        const choice = this.choices[choiceID] as LocatableDataChoice ?? null;
        if(!(choice instanceof LocateDataChoice)){
            return;
        }
        if(!choice || choice.referenceNotFound){
            return;
        }
        return this.requestReferencesNear(choiceID).then();
    }

    async requestReferencesNear(primaryChoiceID:string):Promise<LocatableDataChoice[]>
    {
        if(!this.requestReferenceLimit){
            return [];
        }
        if(this.requestReferenceLimit < 0){
            return this.requestReferences();
        }
        const primaryChoice = this.choices[primaryChoiceID] as LocatableDataChoice ?? null;
        const lngLat = primaryChoice?.lngLat ?? null;

        const distances:INumberObject = {};
        const choiceList = this.choiceList().slice();
        for(let choice of choiceList){
            if(choice.id === primaryChoiceID){
                distances[choice.id] = 0;
            } else if(lngLat && choice.lngLat){
                distances[choice.id] = choice.getDistanceInMetres(lngLat)!;
            }
        }

        choiceList.sort((a, b) => {
            if(a.id === primaryChoiceID){
                return  -1;
            }
            if(b.id === primaryChoiceID){
                return  1;
            }
            const aDistance = distances[a.id] ?? null;
            const bDistance = distances[b.id] ?? null;
            if(aDistance !== null && bDistance === null){
                return  -1;
            }
            if(aDistance === null && bDistance !== null){
                return  1;
            }
            if(aDistance !== null){
                if(aDistance < bDistance){
                    return  -1;
                }
                if(aDistance > bDistance){
                    return  1;
                }
            }
            let aIndex = this.choiceOrder.indexOf(a.id);
            let bIndex = this.choiceOrder.indexOf(b.id);

            return aIndex <= bIndex ? -1 : 1;
        });

        return this.requestReferences(choiceList);
    }


    async onPopulated(): Promise<void>
    {
        if(!this.requestReferenceLimit){
            return
        }

        if(this.requestReferenceLimit > 0){
            const requiredReferenceIDs = this.getRequiredReferenceIDs(null, this.requestReferenceLimit + 1);
            if(requiredReferenceIDs.length > this.requestReferenceLimit){
                this.applyReferenceChoices()
                return;
            }
        }
        return this.requestReferences().then()
    }

    getSourceReferenceModel():LocatableDataModel|null
    {
        return getModelFactory().postData.getSharedDataModel(this.searchParams.postType ?? "organisation");
    }

    applyChoice(choice: LocatableDataChoice)
    {
        super.applyChoice(choice);
        choice.countryCode = this.searchParams.countryCode ?? choice.countryCode;
        choice.postType = this.searchParams.postType ?? choice.postType;
    }

    choiceList(choiceOrder?:string[]): LocatableDataChoice[]
    {
        return super.choiceList(choiceOrder) as LocatableDataChoice[];
    }

    populateWithRequestResponse(response: IAnyObject, replaceData:boolean): ApiError | null
    {
        let error =  super.populateWithRequestResponse(response, replaceData);
        if(error){
            return error;
        }
        const objectArray:IAnyObject[] = response.entries ?? [];

        const parseObjectArray:IAnyObject[] = [];
        if(this.returnType === "reference_object" || this.returnType === "basic_reference_object"){
            const sharedModel = this.getSourceReferenceModel();
            if(sharedModel){

                const choiceList:LocatableDataChoice[] = [];
                for(let i = 0; i < objectArray.length; i++)
                {
                    const object = objectArray[i];
                    const fromChoice = sharedModel.createChoice(object);
                    if(fromChoice)
                    {
                        choiceList.push(fromChoice);
                    }
                }
                sharedModel.addToChoiceList(choiceList, false);
                this.addToChoiceList(choiceList, replaceData);
                this.applyResponseToRelatedPages(response);
            }


            return null;
        }

        if(this.returnType === "points_with_ids"){
            for(let i = 0; i < objectArray.length; i++){
                const object = objectArray[i];
                const latitude = parseFloat(object.y ?? 0);
                const longitude = parseFloat(object.x ?? 0);
                if(!latitude && !longitude){
                    continue;
                }
                const referenceIDs:string[] = (object.i ?? "").split(",");
                for(let i = 0; i < referenceIDs.length; i++){
                    const referenceID = referenceIDs[i].trim();
                    const id = referenceID.toString();
                    parseObjectArray.push({
                        id: id,
                        reference_id: referenceID,
                        latitude: latitude,
                        longitude: longitude
                    });
                }
            }
        }
        this.populateChoiceList(parseObjectArray, replaceData);
        this.applyResponseToRelatedPages(response);


        return null;
    }


    requireChoiceOrder(): void
    {
        super.requireChoiceOrder();
        if(this.searchParams.sortBy === "north_west_distance"){
            let bounds:LngLatBounds|null = null;
            const choiceList:LocatableDataChoice[] = [];
            for(let choiceID in this.choices){
                const choice = this.choices[choiceID];
                choiceList.push(choice)
                if(choice.lngLat){
                    if(!bounds){
                        bounds = new LngLatBounds(choice.lngLat, choice.lngLat);
                    } else{
                        bounds.extend(choice.lngLat);
                    }
                }
            }
            if(!bounds)
            {
                return
            }

            const distances:INumberObject = {};
            for(let choice of choiceList){
                if(choice.lngLat){
                    distances[choice.id] = choice.getDistanceInMetres(bounds.getNorthWest())!;
                }
            }

            choiceList.sort((a, b) => {
                const aDistance = distances[a.id] ?? null;
                const bDistance = distances[b.id] ?? null;
                if(aDistance !== null && bDistance === null){
                    return  -1;
                }
                if(aDistance === null && bDistance !== null){
                    return  1;
                }
                if(aDistance !== null){
                    if(aDistance < bDistance){
                        return  -1;
                    }
                    if(aDistance > bDistance){
                        return  1;
                    }
                }
                let aIndex = this.choiceOrder.indexOf(a.id);
                let bIndex = this.choiceOrder.indexOf(b.id);

                return aIndex <= bIndex ? -1 : 1;
            });
            this.choiceOrder.length = 0;
            for(let choice of choiceList){
                this.choiceOrder.push(choice.id);
            }
        }
    }

    applyResponseToRelatedPages = (response: IAnyObject) => {

        const locateModels = this.getPageChildModels();
        if(!locateModels.length){
            return;
        }

        let previous = this.pageNumber > 1 ? (response.previous ?? "") + "" : "";
        let next = (response.next ?? "") + "";

        previous = isNumeric(previous) && !parseInt(previous) ? "" : previous;
        next = isNumeric(next) && !parseInt(next) ? "" : next;

        let valid = (!next && (this.pageNumber === locateModels.length)) || (!!next && (this.pageNumber < locateModels.length));
        let canRetry = true;
        if(valid){
            for(let i = 0; i < locateModels.length; i++)
            {
                const locateModel = locateModels[i];
                if((locateModel.pageNumber === this.pageNumber) || !locateModel.isPopulated())
                {
                    continue;
                }
                if(locateModel.pageNumber === (this.pageNumber - 1)){
                    const lastChoiceID = locateModel.choiceOrder[locateModel.choiceOrder.length - 1] ?? "";
                    if(lastChoiceID !== previous){
                        valid = false;
                        if(!lastChoiceID){
                            canRetry = false;
                        }
                        break;
                    }
                }
                if(locateModel.pageNumber === (this.pageNumber + 1)){
                    const firstChoiceID = locateModel.choiceOrder[0] ?? "";
                    if(firstChoiceID !== next){
                        valid = false;
                        if(!firstChoiceID){
                            canRetry = false;
                        }
                        break;
                    }
                }
                for(let j = 0; j < locateModel.choiceOrder.length; j++){
                    if(this.choiceOrder.includes(locateModel.choiceOrder[i])){
                        valid = false;
                        break;
                    }
                }
                if(!valid){
                    break;
                }
            }
        }


        if(!valid && canRetry){
            this.getPageModel()?.resetPopulated();
            for(let i = 0; i < locateModels.length; i++)
            {
                const locateModel = locateModels[i];
                if((locateModel.pageNumber === this.pageNumber))
                {
                    continue;
                }
                locateModel.resetPopulated();
            }
        }
    }

    withLocationChoiceList(): LocatableDataChoice[]
    {
        if(!this.activeWithLocationChoiceList){
            this.activeWithLocationChoiceList = this.service().createChoiceList(this.choices, this.requireWithLocationChoiceOrder()) as LocatableDataChoice[];
        }
        return this.activeWithLocationChoiceList;
    }

    clearActiveChoiceList()
    {
        this.withLocationChoiceOrder = null;
        this.activeWithLocationChoiceList = null;
        super.clearActiveChoiceList();
    }

    requireWithLocationChoiceOrder(): string[]
    {
        if(this.withLocationChoiceOrder !== null){
            return this.withLocationChoiceOrder;
        }
        this.withLocationChoiceOrder = [];
        for(let choice of this.choiceList()){
            if(choice.lngLat){
                this.withLocationChoiceOrder.push(choice.id)
            }
        }
        return this.withLocationChoiceOrder;
    }


    getCountryModel(): LocateCountryDataModel
    {
        return getModelFactory().locateCountryData.getLocateCountryDataModel( this.searchParams );
    }

    getPageChildModels(): SearchBasicDataModel[]
    {
        return this.getPageModel()?.getChildModels(this.returnType) ?? [];
    }

    getPageModel(): LocatePageListDataModel|null
    {
        if(!this.pageNumber){
            return null;
        }
        return getModelFactory().locatePageListData.getLocatePageListDataModel(this.searchParams);
    }
}

export default class LocateDataFactory extends LocatableDataFactory
{

    id: FactoryID = "locate_data";

    birth(id: string): LocateDataModel
    {
        return new LocateDataModel(id);
    }

    birthDataChoice(object:IAnyObject): LocateDataChoice
    {
        return new LocateDataChoice(object);
    }

    getLocateDataModel(searchParams:LocateSearchParams, pageNumber:number, returnType?:LocateReturnType): LocateDataModel
    {
        const populateIDs:IPopulateIDs = {
            post_type: searchParams.postType ?? undefined,
            search_sort_by: searchParams.sortBy,
        };
        if(searchParams.searchTermID){
            populateIDs.search_term_id = searchParams.searchTermID;
        }
        if(searchParams.countryCode)
        {
            populateIDs.locate_country_code = searchParams.countryCode;
        }
        if(searchParams.latitude || searchParams.longitude)
        {
            populateIDs.locate_latitude = searchParams.latitude;
            populateIDs.locate_longitude = searchParams.longitude;
        }
        if(searchParams.requireLocation)
        {
            populateIDs.locate_require_location = true;
        }
        if(pageNumber){
            populateIDs.page_number = pageNumber;
        }
        if(returnType){
            populateIDs.locate_return_type = returnType;
        }
        return this.requireModel(populateIDs) as LocateDataModel;
    }

    getMapLocateDataModel(postType:PostType|null, countryCode:CountryCode|""): LocateDataModel
    {
        const searchParams:LocateSearchParams = new LocateSearchParams();
        searchParams.postType = postType ?? "organisation";
        searchParams.sortBy = "north_west_distance";
        searchParams.searchTermID = "search_all";
        searchParams.countryCode = countryCode;
        searchParams.requireLocation = true;
        return this.getLocateDataModel(searchParams, 0, "points_with_ids");
    }
}